import {
  getObject,
  getBoolean,
  getOptionalBoolean,
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "./code";
import { Answer, AnswerDTO } from "./answer";
import { SingleChoiceAnswerV1, SingleChoiceAnswerV1DTO } from "./singleChoiceAnswerV1";
import { MultiChoiceAnswerV1, MultiChoiceAnswerV1DTO } from "./multiChoiceAnswerV1";


export namespace GridAnswerElementV1Enums {
  export namespace AnswerElement {
    export interface OpenEndedDTO {
      case: string;
      openEndedAnswerAttributes: AnswerDTO;
    }
    
    export class OpenEnded {
      readonly isAnswerElementOpenEnded: boolean = true;
    
      readonly openEndedAnswerAttributes: Answer;
    
    
      constructor(
      openEndedAnswerAttributes: Answer
      ) {
        this.openEndedAnswerAttributes = openEndedAnswerAttributes;
      }
    
      static fromDTO(dto: OpenEndedDTO): OpenEnded {
        const openEndedAnswerAttributes = Answer.fromDTO(getObject(dto, "openEndedAnswerAttributes", InvalidGridAnswerElementV1AnswerElementOpenEndedOpenEndedAnswerAttributesError));
        return new OpenEnded(
          openEndedAnswerAttributes
        );
      }
    
      toDTO(): OpenEndedDTO {
        const dto: OpenEndedDTO = {
          case: "OPEN_ENDED",
            openEndedAnswerAttributes: this.openEndedAnswerAttributes.toDTO()
        };
        return dto;
      }
      }
    
    OpenEnded.prototype.toString = function toString(): string {
      return `AnswerElement.OPEN_ENDED(openEndedAnswerAttributes='${this.openEndedAnswerAttributes}')`;
    };
    
    export interface SingleChoiceDTO {
      case: string;
      singleChoiceAnswerAttributes: SingleChoiceAnswerV1DTO;
    }
    
    export class SingleChoice {
      readonly isAnswerElementSingleChoice: boolean = true;
    
      readonly singleChoiceAnswerAttributes: SingleChoiceAnswerV1;
    
    
      constructor(
      singleChoiceAnswerAttributes: SingleChoiceAnswerV1
      ) {
        this.singleChoiceAnswerAttributes = singleChoiceAnswerAttributes;
      }
    
      static fromDTO(dto: SingleChoiceDTO): SingleChoice {
        const singleChoiceAnswerAttributes = SingleChoiceAnswerV1.fromDTO(getObject(dto, "singleChoiceAnswerAttributes", InvalidGridAnswerElementV1AnswerElementSingleChoiceSingleChoiceAnswerAttributesError));
        return new SingleChoice(
          singleChoiceAnswerAttributes
        );
      }
    
      toDTO(): SingleChoiceDTO {
        const dto: SingleChoiceDTO = {
          case: "SINGLE_CHOICE",
            singleChoiceAnswerAttributes: this.singleChoiceAnswerAttributes.toDTO()
        };
        return dto;
      }
      }
    
    SingleChoice.prototype.toString = function toString(): string {
      return `AnswerElement.SINGLE_CHOICE(singleChoiceAnswerAttributes='${this.singleChoiceAnswerAttributes}')`;
    };
    
    export interface MultiChoiceDTO {
      case: string;
      multiChoiceAnswerAttributes: MultiChoiceAnswerV1DTO;
    }
    
    export class MultiChoice {
      readonly isAnswerElementMultiChoice: boolean = true;
    
      readonly multiChoiceAnswerAttributes: MultiChoiceAnswerV1;
    
    
      constructor(
      multiChoiceAnswerAttributes: MultiChoiceAnswerV1
      ) {
        this.multiChoiceAnswerAttributes = multiChoiceAnswerAttributes;
      }
    
      static fromDTO(dto: MultiChoiceDTO): MultiChoice {
        const multiChoiceAnswerAttributes = MultiChoiceAnswerV1.fromDTO(getObject(dto, "multiChoiceAnswerAttributes", InvalidGridAnswerElementV1AnswerElementMultiChoiceMultiChoiceAnswerAttributesError));
        return new MultiChoice(
          multiChoiceAnswerAttributes
        );
      }
    
      toDTO(): MultiChoiceDTO {
        const dto: MultiChoiceDTO = {
          case: "MULTI_CHOICE",
            multiChoiceAnswerAttributes: this.multiChoiceAnswerAttributes.toDTO()
        };
        return dto;
      }
      }
    
    MultiChoice.prototype.toString = function toString(): string {
      return `AnswerElement.MULTI_CHOICE(multiChoiceAnswerAttributes='${this.multiChoiceAnswerAttributes}')`;
    };
    
  
    export type AnswerElement = OpenEnded | SingleChoice | MultiChoice;
  
    export type AnswerElementDTO = OpenEndedDTO | SingleChoiceDTO | MultiChoiceDTO;
  
    export const fromDTO = (dto: AnswerElementDTO): AnswerElement => {
      switch (dto.case) {
        case "OPEN_ENDED":
            return OpenEnded.fromDTO(dto as OpenEndedDTO);
        case "SINGLE_CHOICE":
            return SingleChoice.fromDTO(dto as SingleChoiceDTO);
        case "MULTI_CHOICE":
            return MultiChoice.fromDTO(dto as MultiChoiceDTO);
        default:
          throw new InvalidGridAnswerElementV1AnswerElementError(
              `Case ${dto.case} is not valid case of AnswerElement.`
          );
      }
    };
  }
  
}
export interface GridAnswerElementV1DTO {
  rowCode: CodeDTO;
  columnCode: CodeDTO;
  isUnanswered?: boolean | null;
  answerElement: GridAnswerElementV1Enums.AnswerElement.AnswerElementDTO;
}

export class GridAnswerElementV1 {
  readonly isGridAnswerElementV1GridAnswerElementV1: boolean = true;

  readonly rowCode: Code;
  readonly columnCode: Code;
  readonly isUnanswered: boolean | null;
  readonly answerElement: GridAnswerElementV1Enums.AnswerElement.AnswerElement;


  constructor(
  rowCode: Code, 
  columnCode: Code, 
  isUnanswered: boolean | null = null, 
  answerElement: GridAnswerElementV1Enums.AnswerElement.AnswerElement
  ) {
    this.rowCode = rowCode;
    this.columnCode = columnCode;
    this.isUnanswered = isUnanswered;
    this.answerElement = answerElement;
  }

  static fromDTO(dto: GridAnswerElementV1DTO): GridAnswerElementV1 {
    const rowCode = Code.fromDTO(getObject(dto, "rowCode", InvalidGridAnswerElementV1RowCodeError));
    const columnCode = Code.fromDTO(getObject(dto, "columnCode", InvalidGridAnswerElementV1ColumnCodeError));
    const isUnanswered = getOptionalBoolean(dto, "isUnanswered", InvalidGridAnswerElementV1IsUnansweredError);
    const answerElement = GridAnswerElementV1Enums.AnswerElement.fromDTO(getObject(dto, "answerElement", InvalidGridAnswerElementV1AnswerElementError));
    return new GridAnswerElementV1(
      rowCode,
      columnCode,
      isUnanswered,
      answerElement
    );
  }

  toDTO(): GridAnswerElementV1DTO {
    const dto: GridAnswerElementV1DTO = {
        rowCode: this.rowCode.toDTO(),
        columnCode: this.columnCode.toDTO(),
        answerElement: this.answerElement.toDTO()
    };
    if (this.isUnanswered !== undefined && this.isUnanswered !== null){
      dto.isUnanswered = this.isUnanswered;
    }
    return dto;
  }
  }

GridAnswerElementV1.prototype.toString = function toString(): string {
  return `GridAnswerElementV1(rowCode='${this.rowCode}',columnCode='${this.columnCode}',isUnanswered='${this.isUnanswered}',answerElement='${this.answerElement}')`;
};

export class InvalidGridAnswerElementV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1Error";
  }
}
export class InvalidGridAnswerElementV1RowCodeError extends InvalidGridAnswerElementV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1RowCodeError";
  }
}

export class InvalidGridAnswerElementV1ColumnCodeError extends InvalidGridAnswerElementV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1ColumnCodeError";
  }
}

export class InvalidGridAnswerElementV1IsUnansweredError extends InvalidGridAnswerElementV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1IsUnansweredError";
  }
}

export class InvalidGridAnswerElementV1AnswerElementError extends InvalidGridAnswerElementV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1AnswerElementError";
  }
}

export class InvalidGridAnswerElementV1AnswerElementOpenEndedError extends InvalidGridAnswerElementV1AnswerElementError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1AnswerElementOpenEndedError";
  }
}

export class InvalidGridAnswerElementV1AnswerElementOpenEndedOpenEndedAnswerAttributesError extends InvalidGridAnswerElementV1AnswerElementOpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1AnswerElementOpenEndedOpenEndedAnswerAttributesError";
  }
}

export class InvalidGridAnswerElementV1AnswerElementSingleChoiceError extends InvalidGridAnswerElementV1AnswerElementError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1AnswerElementSingleChoiceError";
  }
}

export class InvalidGridAnswerElementV1AnswerElementSingleChoiceSingleChoiceAnswerAttributesError extends InvalidGridAnswerElementV1AnswerElementSingleChoiceError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1AnswerElementSingleChoiceSingleChoiceAnswerAttributesError";
  }
}

export class InvalidGridAnswerElementV1AnswerElementMultiChoiceError extends InvalidGridAnswerElementV1AnswerElementError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1AnswerElementMultiChoiceError";
  }
}

export class InvalidGridAnswerElementV1AnswerElementMultiChoiceMultiChoiceAnswerAttributesError extends InvalidGridAnswerElementV1AnswerElementMultiChoiceError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridAnswerElementV1AnswerElementMultiChoiceMultiChoiceAnswerAttributesError";
  }
}
